import React, { useState, useEffect } from 'react';
import './shop.css';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useCart } from '../../CartContext/CartContext';
import CartPopup from '../../components/CartPopup';

// Funktion til at håndtere både markdown og HTML-indhold
const prepareContent = (content) => {
  if (!content) return '';

  // Hvis teksten allerede er HTML (fra rich text editor)
  if (content.includes('<p>') || content.includes('<h') || content.includes('<ul>') || 
      content.includes('<strong>') || content.includes('<u>') || content.includes('<a ')) {
    // Vi beholder HTML som den er, da formatering håndteres korrekt i rich text editor
    return content;
  }

  // Hvis det er markdown eller plain text
  // Konverter overskrifter
  let html = content.replace(/^## (.*?)$/gm, '<h3>$1</h3>');
  html = html.replace(/^# (.*?)$/gm, '<h2>$1</h2>');

  // Konverter fed tekst
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

  // Konverter bullet points
  html = html.replace(/^- (.*?)$/gm, '<li>$1</li>');
  
  // Del teksten på dobbelte linjeskift, hvilket normalt indikerer nye afsnit
  const paragraphs = html.split(/\n\s*\n/); // Split ved tomme linjer
  html = paragraphs.map(p => {
    const trimmedP = p.trim();
    if (trimmedP === '') return '';
    
    // Håndter lister
    if (trimmedP.includes('<li>')) {
      return `<ul>${trimmedP}</ul>`;
    }
    
    // Håndter overskrifter
    if (trimmedP.includes('<h3>') || trimmedP.includes('<h2>')) {
      return trimmedP;
    }
    
    // Almindelige afsnit - bevar enkelte linjeskift som mellemrum
    return `<p>${trimmedP.replace(/\n/g, ' ')}</p>`;
  }).join('\n\n'); // Tilføj afstand mellem afsnit

  return html;
};

const Shop = () => {
  const { addToCart, showPopup, lastAddedProduct, lastAddedQuantity, hidePopup } = useCart();
  
  const [shopData, setShopData] = useState({
    title: 'Shop',
    heroImage: '/media/nyheder/følelseskort.avif',
    introText: 'I vores shop kan du købe materialer, der kan bruges i terapeutisk arbejde med børn, unge og familier. Alle vores produkter er udviklet med udgangspunkt i vores erfaring med mentaliseringsbaseret terapi. For at bestille, kontakt os venligst via mail eller telefon med information om hvilket produkt du ønsker.',
    orderText: 'For at bestille et produkt fra vores shop, beder vi dig sende en mail til admin@psykologiskperspektiv.dk med oplysninger om hvilket produkt du ønsker at købe, samt dit navn og leveringsadresse. Vi vil derefter kontakte dig vedrørende betaling og levering. Ved bestilling bedes du angive produktnavn og antal i emnefeltet.'
  });
  
  const [produkter, setProdukter] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchShopData = async () => {
      try {
        // Fetch shop content
        const shopDocRef = doc(db, 'content', 'shop');
        const shopDoc = await getDoc(shopDocRef);
        
        if (shopDoc.exists()) {
          setShopData({
            title: shopDoc.data().title || 'Shop',
            heroImage: shopDoc.data().heroImage || '/media/nyheder/følelseskort.avif',
            introText: shopDoc.data().introText || '',
            orderText: shopDoc.data().orderText || ''
          });
        }
        
        // Fetch products
        const produkterDocRef = doc(db, 'content', 'produkter');
        const produkterDoc = await getDoc(produkterDocRef);
        
        if (produkterDoc.exists() && produkterDoc.data().items) {
          setProdukter(produkterDoc.data().items);
        } else {
          // Fallback til standard produkter hvis data ikke findes
          setProdukter([
    {
      id: 'foelelseskort',
      navn: 'Følelseskort',
      pris: '599 kr.',
      kort_beskrivelse: 'Til brug i terapi og samtaler med børn, unge og familier.',
      billede: '/media/nyheder/følelseskort.avif',
      slug: '/shop/foelelseskort'
    },
    {
      id: 'mbt-bogen',
      navn: 'Mentalisering i børnehøjde',
      pris: '299 kr.',
      kort_beskrivelse: 'En bog om hvordan mentalisering kan bruges i samtaler med børn.',
      billede: '/media/hero/MBT-boger.avif',
      slug: '/shop/mbt-bogen'
    },
    {
      id: 'folgeskab',
      navn: 'Følgeskab med barnet',
      pris: '349 kr.',
      kort_beskrivelse: 'En bog om hvordan vi som voksne følger barnets følelsesmæssige udvikling.',
      billede: '/media/hero/MBT-klodser.avif',
      slug: '/shop/folgeskab'
    }
          ]);
        }
      } catch (error) {
        console.error('Fejl ved hentning af shop data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchShopData();
  }, []);

  const handleQuickAddToCart = (produkt) => {
    addToCart(produkt, 1);
  };

  if (isLoading) {
    return <div className="shop-oversigt-loading">Indlæser...</div>;
  }

  return (
    <div className="shop-oversigt-side">
      <div className="shop-oversigt-container">
        <h1>{shopData.title}</h1>
        
        <div className="shop-oversigt-hero">
          <div className="shop-oversigt-hero-content">
            <div className="shop-oversigt-hero-billed">
              <img src={shopData.heroImage} alt="Shop hos Psykologisk Perspektiv" />
            </div>
            <div dangerouslySetInnerHTML={{ __html: prepareContent(shopData.introText) }} />
          </div>
        </div>

        <div className="shop-oversigt-section">
          <h3>Bestilling</h3>
          <div dangerouslySetInnerHTML={{ __html: prepareContent(shopData.orderText) }} />
        </div>

        <div className="shop-oversigt-produkter">
          {produkter.map(produkt => (
            <article key={produkt.id} className="shop-oversigt-kort">
              <div className="shop-oversigt-venstre">
                <img src={produkt.billede} alt={produkt.navn} />
              </div>
              <div className="shop-oversigt-højre">
                <h2>{produkt.navn}</h2>
                <p className="shop-oversigt-pris">{produkt.pris}</p>
                <p>{produkt.kort_beskrivelse}</p>
                <div className="shop-oversigt-knapper">
                  <button className="shop-oversigt-tilfoej-knap" onClick={() => handleQuickAddToCart(produkt)}>
                    Tilføj til kurv
                  </button>
                  <Link to={produkt.slug || `/shop/${produkt.id}`} className="shop-oversigt-detaljer-knap">
                    Se detaljer
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
      
      <CartPopup 
        isVisible={showPopup}
        product={lastAddedProduct}
        quantity={lastAddedQuantity}
        onClose={hidePopup}
        onGoToCart={hidePopup}
      />
    </div>
  );
};

export default Shop;
